/**
 * PersonelBelgeKutusu.jsx — denetmen kişi sayfasının (PersonelDetayPage) Belgeler sekmesindeki belge kutuları.
 * Backlog #284 (2026-10-06).
 *
 * Sayfanın üstündeki renkli kare şeridi kalktı (İstenen Belgeler ile aynı bilgiyi ikinci kez veriyordu);
 * renkli kutu artık kutunun KENDİ simgesi, düğmeler de altında — firma panelinin FirmaPersonelDetailPage'indeki
 * BelgeCard ile aynı görünüm. Firma panelindekinin aksine burada yazma düğmeleri denetmen içindir:
 * İncele (dosya varsa), Yükle / Yeniden Yükle (onaylıda da: basılmış bir kart yüklemeyi engellemez, #279).
 *
 * `PersonelBelgeKutusu`  — kişinin bir belgesi (KVKK dahil).
 * `FirmaBelgelerKarti`   — kişinin firmasının belge grupları (Firma Belgeleri + Firma İSG Belgeleri), salt okunur,
 *                          kişisel belgelerin yanında. Gruplar `person.firmaBelgeGruplari[]`'dan, kural backend'de.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader } from "reactstrap";
import { Building2, ClipboardCheck, FileCheck2 } from "lucide-react";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import { BELGE_TIP, donemYaz, paylasimliMi } from "../../services/personelBelgeService";
import { siraliBelgeGruplari, grupBelgeKareleri, grupDurumInfo, grupKartNotu, uygulanamazDurumu } from "../Firma/belgeGruplari";
import { firmaBelgeleriUrl } from "./kisiBaglantilari";
import BelgeSablonuBaglantilari from "../Common/BelgeSablonuBaglantilari";
import { useTranslation } from "react-i18next";

const durumRengi = (status) => {
  const info = status != null ? FILE_APPROVAL_STATUS[status] : null;
  return info ? STATUS_HEX[info.color] ?? NEUTRAL_GRAY : NEUTRAL_GRAY;
};

/**
 * @param label        belgenin adı
 * @param Icon         lucide simgesi
 * @param doc          `documents[anahtar]` (yoksa "Kayıt Yok")
 * @param belgeKey     DOM kimliği (`belge-karti-<anahtar>`): Kişi Listesi'nden gri kareyle gelinince kutu vurgulanır (#273)
 * @param vurgu        bu kutu vurgulansın
 * @param soluk        istenmeyen ("Diğer yüklenmiş") belge
 * @param dijitalOnay  KVKK'nın üçüncü kaynağı (#252): belge onaylı değilse de kutu yeşil okunur
 * @param varyantAdi   kutuyu dolduran belgenin türü (İşe Giriş Bildirgesi / Hizmet Dökümü …)
 * @param tasimaNotu   "taşındı" notu
 * @param onIncele     inceleme penceresi; dosyası olmayan kutuda İncele yok
 * @param onYukle      Yükle / Yeniden Yükle; verilmezse düğme yok
 * @param yukleEtiketi `onYukle` için sabit yazı (varsayılan: Yükle / Yeniden Yükle)
 * @param sablon       kurumun bu belge için verdiği { bosForm?, ornek? } (#503); yoksa bağlantı çıkmaz
 */
export function PersonelBelgeKutusu({
  label, Icon, doc, belgeKey, vurgu = false, soluk = false, dijitalOnay = false,
  varyantAdi = null, tasimaNotu: not = null, onIncele, onYukle, yukleEtiketi = null, sablon = null,
}) {
  const { t } = useTranslation("components");
  const hasFile = doc?.fileId != null;
  const info = doc?.status != null ? FILE_APPROVAL_STATUS[doc.status] : null;
  const dijitalYesil = dijitalOnay && doc?.status !== 1;
  const background = dijitalYesil ? STATUS_HEX.success : durumRengi(doc?.status);
  const badgeLabel = dijitalYesil ? t("personelBelgeKutusu.dijitalOnay") : info ? info.label : t("personelBelgeKutusu.kayitYok");
  return (
    <Card
      id={belgeKey ? `belge-karti-${belgeKey}` : undefined}
      className={`h-100 mb-0 ${vurgu ? "border border-2 border-primary shadow" : ""}`}
      style={{ opacity: soluk && !hasFile ? 0.6 : 1 }}
    >
      <CardBody className="text-center">
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-3 mb-2"
          role={hasFile ? "button" : undefined}
          title={hasFile ? t("personelBelgeKutusu.belgesiniIncele", { label }) : t("personelBelgeKutusu.kayitYok2", { label })}
          style={{ width: 44, height: 44, background, cursor: hasFile ? "pointer" : undefined }}
          onClick={hasFile ? onIncele : undefined}
        >
          <Icon size={20} color="#fff" />
        </div>
        <div className="small mb-2">{label}</div>
        {varyantAdi && (
          <div className="small text-muted mb-2 d-flex align-items-center justify-content-center gap-1 flex-wrap">
            {varyantAdi}
            {paylasimliMi(doc) && <Badge color="info" className="fw-normal">{t("personelBelgeKutusu.firmaBelgesi")}</Badge>}
          </div>
        )}
        {/* #268: Hizmet Dökümü hangi ayı kapsıyor. Yalnızca bilgi — kartı engellemez (#267); "N ay eski" kartı basan kişiye e-Devlet'i hatırlatır. */}
        {hasFile && doc.fileTypeId === BELGE_TIP.SGK_HIZMET_DOKUMU && (doc.belgeDonemi || doc.belgeDonemiGirilmedi) && (
          <div className="small text-muted mb-2 d-flex align-items-center justify-content-center gap-1 flex-wrap">
            {doc.belgeDonemi
              ? t("paylasimliBelgeler.donem", { v: donemYaz(doc.belgeDonemi) })
              : <Badge color="warning" className="fw-normal">{t("paylasimliBelgeler.donemGirilmedi")}</Badge>}
            {doc.belgeDonemi && doc.belgeDonemiEski && (
              <Badge color="warning" className="fw-normal">{t("paylasimliBelgeler.ayEski", { length: doc.belgeDonemiAyFarki })}</Badge>
            )}
          </div>
        )}
        <Badge
          style={{ background, cursor: hasFile ? "pointer" : undefined }}
          onClick={hasFile ? onIncele : undefined}
        >
          {badgeLabel}
        </Badge>
        {doc?.expired && <div className="small text-warning mt-1">{t("personelBelgeKutusu.suresiDolmus")}</div>}
        {not && <div className="small text-muted fst-italic mt-2">{not}</div>}
        <BelgeSablonuBaglantilari sablon={sablon} />
        {(hasFile || onYukle) && (
          <div className="mt-2 d-flex justify-content-center gap-1 flex-wrap">
            {hasFile && <Button color="light" size="sm" onClick={onIncele}>{t("personelBelgeKutusu.incele")}</Button>}
            {onYukle && (
              <Button color="primary" outline size="sm" onClick={onYukle}>
                {yukleEtiketi ?? (hasFile ? t("personelBelgeKutusu.yenidenYukle") : t("common:yukle"))}
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

const GRUP_IKON = { FIRMA: Building2, ISG: ClipboardCheck };

/**
 * Kişinin firmasının belge grupları — firma panelindeki "Firma İSG Belgeleri" kartının denetmen karşılığı, ama
 * iki grubu da gösterir (İşveren yolu Firma Belgeleri'ne dayanır). Durumlar salt okunur; dosyalar firmanın
 * belgeler sayfasında (bağlantı).
 *
 * @param gruplar   `person.firmaBelgeGruplari`
 * @param firmaAdi  başlıkta yazılan firma
 * @param firmaId, fuarId  "Belgeler" bağlantısı için
 */
export function FirmaBelgelerKarti({ gruplar, firmaAdi, firmaId, fuarId }) {
  const { t } = useTranslation("components");
  const sirali = siraliBelgeGruplari(gruplar || []);
  return (
    <Card className="h-100 mb-0">
      <CardHeader className="bg-white border-bottom">
        <h5 className="mb-0"><Building2 size={18} className="me-2 text-primary" />{t("personelBelgeKutusu.firmaBelgeleri")}</h5>
        {firmaAdi && <div className="small text-muted mt-1 text-truncate" title={firmaAdi}>{firmaAdi}</div>}
      </CardHeader>
      <CardBody>
        {sirali.map((grup, i) => {
          const durum = grup.status == null ? null : grupDurumInfo(grup);
          const GrupIcon = GRUP_IKON[grup.kod] ?? Building2;
          const kartNotu = grupKartNotu(grup);
          return (
            <div key={grup.kod} className={i > 0 ? "mt-3 pt-3 border-top" : ""}>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                <GrupIcon size={15} className="text-muted" />
                <span className="fw-semibold small">{grup.baslik}</span>
                <Badge style={{ background: durum ? STATUS_HEX[durum.color] ?? NEUTRAL_GRAY : NEUTRAL_GRAY }}>
                  {durum ? durum.label : t("personelBelgeKutusu.kayitYok")}
                </Badge>
                {kartNotu && <span className="small text-muted">{kartNotu}</span>}
              </div>
              <div className="d-flex flex-wrap gap-3">
                {grupBelgeKareleri(grup).map((b) => (
                  <div key={b.fileTypeId} style={{ flex: "1 1 120px", maxWidth: 220 }}>
                    <Card className="h-100 mb-0">
                      <CardBody className="text-center py-3">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-3 mb-2"
                          style={{ width: 36, height: 36, background: b.uygulanamaz ? NEUTRAL_GRAY : durumRengi(b.status) }}
                        >
                          <FileCheck2 size={17} color="#fff" />
                        </div>
                        <div className="small mb-2">{b.ad}</div>
                        {/* #442: UYGULANAMAZ kare nötr gri, "Kayıt yok" / "Eksik" okunmaz; nedeni notta. */}
                        <Badge style={{ background: b.uygulanamaz ? NEUTRAL_GRAY : durumRengi(b.status) }}>
                          {b.uygulanamaz
                            ? uygulanamazDurumu().label
                            : b.status != null ? FILE_APPROVAL_STATUS[b.status]?.label ?? "—" : t("personelBelgeKutusu.kayitYok")}
                        </Badge>
                        {b.note && <div className="small text-muted mt-2">{b.note}</div>}
                      </CardBody>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {firmaId != null && (
          <div className="small mt-3">
            <Link to={firmaBelgeleriUrl({ firmaId, fuarId })}>{t("personelBelgeKutusu.firmaninBelgeleri")}</Link>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
